import {PriorityIcon, DatePicker} from '@priority-software/priority-style-react';
import cx from 'classnames';
import React, {RefObject, useEffect, useRef, useState, MouseEvent} from 'react';
import {CalendarContainer} from 'react-datepicker';
import {getFnsLocale, getLang} from '../../../../../Localization';
import {formatDate, formatStringToDate, isDateTime, isMaxLengthReached} from '../../../utils/date';
import {FormInputButton} from '../../FormInputButton';
import usePortal from 'react-useportal';
import {apiLocker, formsStore} from '../../../../../stores/Form';
import {keys} from '../../../../../constants/KeyCodes';
import styles from './dateInput.module.scss';
import {createDataTestId} from '../../../../../utils/dataTestId';

type Props = {
    onChange: (val: string) => void;
    value: string;
    columnType: any;
    inputRef: RefObject<HTMLInputElement>;
    isOpen: boolean;
    setIsOpen: (flag: boolean) => void;
    portalEl?: HTMLElement;
    position?: 'absolute' | 'fixed';
    horizontalShift?: number;
    triggerClassname?: string;
    onClickOutside?: (e: MouseEvent<HTMLElement>) => void;
    dataTestId?: string;
};

const zoomIconWidth = 25;

const isInputTag = (element: HTMLElement) => !!element && element.tagName.toLowerCase() === 'input';

export const FormDatePicker = ({
    onChange,
    value,
    columnType,
    inputRef,
    isOpen,
    setIsOpen,
    portalEl,
    position = 'absolute',
    horizontalShift = zoomIconWidth,
    triggerClassname,
    onClickOutside,
    dataTestId = ''
}: Props) => {
    const {Portal} = usePortal({bindTo: portalEl});
    const [boundingRect, setboundingRect] = useState(inputRef.current?.getBoundingClientRect());
    const inputEl = inputRef.current;
    const datepickerRef = useRef(null);

    useEffect(() => {
        if (inputEl) {
            setboundingRect(inputEl.getBoundingClientRect());
        }
    }, [inputEl]);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === keys.ESC && isOpen) {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(false);
        }
    };

    const dateStringtoStartDate = () => {
        if (value && isMaxLengthReached(value, columnType, getLang())) {
            return formatStringToDate(value, isDateTime(columnType), getLang());
        }
        return new Date();
    };

    const onDateSelected = (date: Date, e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onChange(formatDate(date, columnType));
        setIsOpen(false);
    };

    const CustomContainer = ({className, children}) => {
        const inputMargin = 6;
        const calendarRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            // attach keydown eventlistener to the content of the calendar
            calendarRef.current.addEventListener('keydown', handleKeyDown);
            // block navigation
            const session = formsStore.currentSession;
            apiLocker.lockSession(session, {navigationOnly: true});
            // focus the selected day to enable navigation in calendar
            const parentElement = calendarRef.current;
            if (parentElement) {
                const partialClassName = 'day--selected';
                const elements: NodeListOf<HTMLInputElement> = parentElement.querySelectorAll(
                    `[class*=${partialClassName}]`
                );
                if (elements.length) {
                    elements[0].focus();
                }
            }
            return () => {
                apiLocker.unlockSession(session);
                calendarRef.current?.removeEventListener('keydown', handleKeyDown);

                // focus and select input
                if (isInputTag(inputRef.current)) {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                }
            };
        }, []);

        return (
            <div
                className={cx(styles.datePicker, {
                    [styles.positionFixed]: position === 'fixed',
                    [styles.positionAbsolute]: position === 'absolute'
                })}
                style={{
                    top: boundingRect?.bottom + inputMargin,
                    left: boundingRect?.left - horizontalShift
                }}
                ref={calendarRef}
            >
                <CalendarContainer className={className}>{children}</CalendarContainer>
            </div>
        );
    };

    const showTime = isDateTime(columnType);

    return (
        <>
            <FormInputButton
                componentClasses={triggerClassname}
                icon={PriorityIcon.calendar}
                onClick={(e: MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
            />
            {isOpen && boundingRect && (
                <Portal>
                    <div {...createDataTestId(dataTestId, false)}>
                        <DatePicker
                            datepickerRef={datepickerRef}
                            locale={getFnsLocale(getLang())}
                            selected={dateStringtoStartDate()}
                            onSelect={onDateSelected}
                            onChange={showTime ? onDateSelected : undefined}
                            calendarClassName={styles.calendarContainer}
                            calendarContainer={CustomContainer}
                            inline
                            showTimeSelect={showTime}
                            timeIntervals={15}
                            timeCaption=""
                            onClickOutside={onClickOutside || (() => setIsOpen(false))}
                        />
                    </div>
                </Portal>
            )}
        </>
    );
};
